/**
 * Date formatters that DO NOT shift across timezones.
 *
 * Most of our financial data (installment due_dates, payment_dates,
 * downpayment_date, booking_money_date, future_adjustment.date) represents
 * a CALENDAR DAY — not an instant. Stored either as "YYYY-MM-DD" or
 * "YYYY-MM-DDTHH:MM:SS" (sometimes with Z).
 *
 * `new Date("2026-04-19").toLocaleDateString()` parses as UTC midnight,
 * which renders as "4/18/2026" in any timezone west of UTC. Use these
 * helpers to render the calendar day exactly as stored.
 */

/**
 * Render a stored ISO-ish date string as M/D/YYYY (or the user's locale
 * equivalent) WITHOUT applying any timezone conversion.
 *
 * Accepts:
 *  - "2026-04-19"
 *  - "2026-04-19T00:00:00"
 *  - "2026-04-19T00:00:00.000Z"
 *  - "2026-04-19T00:00:00+00:00"
 *  - falsy (returns '')
 */
export function formatLocalDate(value) {
  if (!value) return '';
  const str = String(value);
  // Pull the YYYY-MM-DD prefix; ignore everything after T (if any).
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    // Not an ISO date — fall back to native handling
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? str : d.toLocaleDateString();
  }
  const [, y, m, d] = match;
  // Construct via numeric args — JS treats this as LOCAL midnight,
  // not UTC midnight, so toLocaleDateString renders the same calendar day.
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString();
}

/**
 * Render a stored ISO-ish datetime as a long human-readable date
 * ("April 19, 2026") without TZ shift.
 */
export function formatLocalDateLong(value) {
  if (!value) return '';
  const str = String(value);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? str : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  const [, y, m, d] = match;
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
